import { useEffect } from 'react';
import { useBreadcrumbs } from '@deloitte-us-consulting/gp-sdk';
import { CornerTicks } from '../../theme/frontDoor/CornerTicks';
import { FdLink } from '../../theme/frontDoor/FdLink';
import { useScrollToTopOnArrival } from '../../theme/frontDoor/useScrollToTopOnArrival';
import { CONTACT_EMAIL, CONTACTS } from '../contact';
import {
  ABOUT_DOCUMENT_TITLE,
  ABOUT_HERO,
  ABOUT_SIGNALS,
  BUILD_SECTION,
  BUILD_STEPS,
  CAPABILITIES,
  CAPABILITIES_SECTION,
  CONTACT_SECTION,
  FIT_LAYERS,
  FIT_SECTION,
  HOOD_SECTION,
  HOOD_TILES,
} from './aboutContent';
import '../../theme/frontDoor/tokens.css';
import '../../theme/frontDoor/frontDoorDark.css';
import './about.css';

const ids = {
  hero: 'gp-fd-about-title',
  caps: 'gp-fd-caps-title',
  fit: 'gp-fd-fit-title',
  build: 'gp-fd-build-title',
  hood: 'gp-fd-hood-title',
  contact: 'gp-fd-contact-title',
} as const;

/**
 * The AI Spine page (`/about`, ADR 0028): what every app on the Growth Platforms App
 * Store gets, how it fits together, how to build one, and who to talk to. Fetches
 * nothing; all copy comes from aboutContent.ts.
 */
export default function AboutSpine() {
  // The SDK's useBreadcrumbs has no cleanup, so the previous page's crumbs and tab
  // title would otherwise linger here. Clear the crumbs and set our own title.
  useBreadcrumbs([]);
  useScrollToTopOnArrival();
  useEffect(() => {
    document.title = ABOUT_DOCUMENT_TITLE;
  }, []);

  return (
    <div className="gp-fd gp-fd-about">
      {/* 1. Hero */}
      <section className="gp-fd-canvas gp-fd-about-hero" aria-labelledby={ids.hero}>
        <span className="gp-fd-ring gp-fd-ring-a" aria-hidden="true" />
        <div className="gp-fd-inner">
          <div className="gp-fd-rise">
            <p className="gp-fd-eyebrow">
              <span aria-hidden="true" />
              {ABOUT_HERO.eyebrow}
            </p>
            <h1 id={ids.hero} className="gp-fd-about-title">
              {ABOUT_HERO.titleLead}
              <br />
              <em>{ABOUT_HERO.titleEmphasis}</em> {ABOUT_HERO.titleTail}
            </h1>
            <p className="gp-fd-lede">{ABOUT_HERO.lede}</p>
            <div className="gp-fd-actions">
              <FdLink to="/onboard" className="gp-fd-button gp-fd-button-primary">
                {ABOUT_HERO.primaryCta}
              </FdLink>
              <a href={`mailto:${CONTACT_EMAIL}`} className="gp-fd-button gp-fd-button-ghost">
                {ABOUT_HERO.secondaryCta}
              </a>
            </div>
          </div>
          <div className="gp-fd-orbit gp-fd-rise gp-fd-rise-2" aria-hidden="true">
            <span className="gp-fd-orbit-ring gp-fd-orbit-ring-outer" />
            <span className="gp-fd-orbit-ring gp-fd-orbit-ring-inner" />
            <div className="gp-fd-orbit-core">
              <small>{ABOUT_HERO.orbitCoreKicker}</small>
              <strong>{ABOUT_HERO.orbitCore}</strong>
            </div>
            {ABOUT_SIGNALS.map((signal, i) => (
              <span key={signal} className={`gp-fd-signal gp-fd-signal-${i}`}>
                {signal}
              </span>
            ))}
            <div className="gp-fd-orbit-card">
              <strong>{ABOUT_HERO.orbitCardTitle}</strong>
              {ABOUT_HERO.orbitCardCaption}
              <div className="gp-fd-orbit-bars">
                <span style={{ height: '40%' }} />
                <span style={{ height: '65%' }} />
                <span style={{ height: '52%' }} />
                <span style={{ height: '90%' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. What every app gets */}
      <section className="gp-fd-paper" aria-labelledby={ids.caps}>
        <div className="gp-fd-inner">
          <p className="gp-fd-kicker">{CAPABILITIES_SECTION.kicker}</p>
          <h2 id={ids.caps} className="gp-fd-section-title">
            {CAPABILITIES_SECTION.title}
          </h2>
          <p className="gp-fd-sub">{CAPABILITIES_SECTION.sub}</p>
          <ul className="gp-fd-caps" aria-labelledby={ids.caps}>
            {CAPABILITIES.map((cap) => (
              <li key={cap.n} className={cap.highlight ? 'gp-fd-cap gp-fd-cap-highlight' : 'gp-fd-cap'}>
                <span className="gp-fd-cap-n">{cap.n}</span>
                <h3>{cap.title}</h3>
                <p>{cap.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 3. How it fits together */}
      <section className="gp-fd-paper" aria-labelledby={ids.fit}>
        <div className="gp-fd-inner">
          <p className="gp-fd-kicker">{FIT_SECTION.kicker}</p>
          <h2 id={ids.fit} className="gp-fd-section-title">
            {FIT_SECTION.title}
          </h2>
          <p className="gp-fd-sub">{FIT_SECTION.sub}</p>
          <div className="gp-fd-stack">
            <CornerTicks />
            {FIT_LAYERS.map((layer, i) => (
              <div key={layer.id}>
                {i > 0 && (
                  <p className="gp-fd-plug" aria-hidden="true">
                    ▲ ▲ ▲ {FIT_SECTION.connector} ▲ ▲ ▲
                  </p>
                )}
                <div className={`gp-fd-layer gp-fd-layer-${layer.id}`}>
                  <span className="gp-fd-layer-label">{layer.label}</span>
                  <ul aria-label={layer.label}>
                    {layer.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Build on the Spine */}
      <section className="gp-fd-paper" aria-labelledby={ids.build}>
        <div className="gp-fd-inner">
          <p className="gp-fd-kicker">{BUILD_SECTION.kicker}</p>
          <h2 id={ids.build} className="gp-fd-section-title">
            {BUILD_SECTION.title}
          </h2>
          <p className="gp-fd-sub">{BUILD_SECTION.sub}</p>
          <ol className="gp-fd-steps">
            {BUILD_STEPS.map((step) => (
              <li key={step.n} className="gp-fd-step">
                <span className="gp-fd-step-n" aria-hidden="true">
                  {step.n}
                </span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
          <div className="gp-fd-actions">
            <FdLink to="/onboard" className="gp-fd-button gp-fd-button-primary">
              {BUILD_SECTION.primaryCta}
            </FdLink>
            <a
              href={BUILD_SECTION.guideUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="gp-fd-button gp-fd-button-outline"
            >
              {BUILD_SECTION.guideCta}
            </a>
          </div>
        </div>
      </section>

      {/* 5. Under the hood */}
      <section className="gp-fd-canvas gp-fd-hood" aria-labelledby={ids.hood}>
        <div className="gp-fd-inner">
          <div>
            <p className="gp-fd-kicker">{HOOD_SECTION.kicker}</p>
            <h2 id={ids.hood} className="gp-fd-section-title">
              {HOOD_SECTION.title}
            </h2>
            <p className="gp-fd-sub">{HOOD_SECTION.sub}</p>
            <FdLink to={HOOD_SECTION.stackHref} className="gp-fd-button gp-fd-button-ghost">
              {HOOD_SECTION.stackCta}
            </FdLink>
          </div>
          <ul className="gp-fd-hood-tiles">
            {HOOD_TILES.map((tile) => (
              <li key={tile.title} className="gp-fd-hood-tile">
                <h3>{tile.title}</h3>
                <p>
                  {tile.code && (
                    <>
                      <code>{tile.code}</code>{' '}
                    </>
                  )}
                  {tile.body}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 6. Contact */}
      <section className="gp-fd-canvas gp-fd-contact" aria-labelledby={ids.contact}>
        <div className="gp-fd-inner">
          <div className="gp-fd-contact-intro">
            <p className="gp-fd-kicker">{CONTACT_SECTION.kicker}</p>
            <h2 id={ids.contact} className="gp-fd-section-title">
              {CONTACT_SECTION.title}
            </h2>
            <p className="gp-fd-sub">{CONTACT_SECTION.sub}</p>
            <a href={`mailto:${CONTACT_EMAIL}`} className="gp-fd-button gp-fd-button-primary">
              {CONTACT_SECTION.cta}
            </a>
            <p className="gp-fd-mailbox">{CONTACT_EMAIL}</p>
          </div>
          {CONTACTS.map((person) => (
            <div key={person.email} className="gp-fd-person">
              <span className="gp-fd-avatar" aria-hidden="true">
                {person.initials}
              </span>
              <h3>{person.name}</h3>
              <p className="gp-fd-person-role">{person.role}</p>
              <a href={`mailto:${person.email}`} className="gp-fd-person-email">
                {person.email}
              </a>
              <p className="gp-fd-person-ask">
                <span>
                  {CONTACT_SECTION.askAboutPrefix} {person.name.split(' ')[0]} {CONTACT_SECTION.askAboutSuffix}
                </span>
                {person.askAbout}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
